"use client"

import { useEffect, useState, useSyncExternalStore } from "react"
import Link from "next/link"
import { Ellipsis, LogIn } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Kbd } from "@/components/ui/kbd"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { SidebarTrigger, useSidebar } from "@/components/ui/custom-sidebar"
import { UserButton } from "@/components/nav/UserButton"
import { WordmarkMenu } from "@/components/nav/WordmarkMenu"
import { AppControls } from "@/components/layout/AppControls"
import { HistoryPanel } from "@/components/nav/HistoryPanel"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { PAGE_HEADER_ROW_SLOT_ID, PAGE_HEADER_SLOT_ID, PAGE_INSET, slotRef } from "@/components/layout/PageSlots"
import { ToolbarDivider } from "@/components/layout/ToolbarDivider"
import { CHROME_RAMP_PX, CHROME_ROW_FADE, CHROME_ROW_PX, chromeRow } from "@/lib/fade"
import { CHROME_ROW, EASE } from "@/lib/motion"
import { useUser } from "@/context/UserContext"
import { SUBSCRIPTIONS_OPEN } from "@/config/launch"
import { useTranslations } from "next-intl"

/**
 * The app's one header. Every route wears it — the research canvas, the reader,
 * the marketing pages, the auth pages — because the alternative, which this
 * replaces, was three headers that drifted: a marketing SiteHeader, the shell's
 * own bar, and a set of controls the research canvas drew for itself.
 *
 * Fixed and transparent, with content running underneath and dissolving into it —
 * see CHROME_MASK in lib/fade.ts. It used to raise a shadow once the scrollport
 * moved; the fade replaces that, and does it without a scroll listener.
 *
 * Three zones, left to right:
 *   left    wordmark, which is also the trigger for the nav panel
 *   middle  PAGE_HEADER_SLOT — whichever page is mounted portals its controls here
 *   right   command menu, feedback, theme, account (or the signed-out CTAs)
 *
 * The middle zone is a portal target rather than a prop because the layout renders
 * one header for every route and can't know which page is below it. See
 * components/layout/page-slots.tsx.
 */
export function AppHeader({
  /**
   * `minimal` drops the sidebar toggle, the page slot and the app tools — for
   * sign-in and sign-up, where the only thing on offer is getting back to the app.
   */
  variant = "app",
  className,
}: {
  variant?: "app" | "minimal"
  className?: string
}) {
  const t = useTranslations("actions")
  const tToolbar = useTranslations("reader.toolbar")
  const [commandOpen, setCommandOpen] = useState(false)
  const user = useUser()
  const { open } = useSidebar()

  const isApp = variant === "app"

  // Whether a page has opened the second row. See PageHeaderRow.
  const rowOpen = useSyncExternalStore(chromeRow.subscribe, chromeRow.isOpen, () => false)

  /*
    The row's height, as a custom property on the root, for CSS that has to clear the header —
    the scrollport's scroll-padding, so an in-page jump lands below the row rather than under it.
    Script that measures reads `chromeClearancePx()` instead.
  */
  useEffect(() => {
    document.documentElement.style.setProperty("--chrome-row", rowOpen ? `${CHROME_ROW_PX}px` : "0px")
  }, [rowOpen])

  return (
    <>
      <header
        className={cn(
          // On a phone the page's ⋯ and the account button are all there is, side by side like any two
          // buttons in a group — no rule between them to make room for.
          "group/header fixed inset-x-0 top-0 z-30 h-16 flex flex-row items-center gap-6 max-sm:gap-2",
        PAGE_INSET,
          className,
        )}
      >
        <div className="flex shrink-0 flex-row items-center gap-3">
          {/* {isApp && (
            <Tooltip>
              <TooltipTrigger asChild>
                <SidebarTrigger variant="secondary" size="icon" />
              </TooltipTrigger>
              <TooltipContent>
                {open ? "Hide" : "Show"} sidebar <Kbd>⌘ b</Kbd>
              </TooltipContent>
            </Tooltip>
          )} */}

          {/*
            The wordmark is the app's primary navigation — see WordmarkMenu for why
            it lives here and not in the page slot. On the minimal variant it stays
            what it always was: a way back to the app from a sign-in form.
          */}
          {isApp ? (
            <WordmarkMenu />
          ) : (
            <Link
              href="/"
              className="text-2xl font-bold leading-none whitespace-nowrap"
            >
              {t("lawlibraryAi")}
            </Link>
          )}
        </div>

        {/*
            Page controls. `min-w-0` + horizontal scroll so a toolbar-heavy page
            (the reader has a dozen toggles) shrinks rather than pushing the
            account button off the right edge on a narrow window.

            RIGHT ALIGNED, which a page's controls have to be laid out for: the
            row's right edge is what stays put, so a control holds its position
            only while everything that appears and disappears is to its LEFT.
            Anything that decides whether another control exists therefore goes
            to the RIGHT of what it reveals — otherwise pressing it removes a
            neighbour and slides the control itself out from under the pointer,
            and a second press lands on whatever moved into its place. */}
        {/*
          The second row's background and the fade beneath it, drawn over the page.

          Behind everything else in the header, and slid down into place rather than grown: a
          transform runs on the compositor, so the header opens smoothly however busy the page
          is, where animating a height — or the scrollport's mask — would repaint the page on
          every frame. At rest it sits up behind the first row, whose band the scrollport's own
          mask already keeps clear, so it is never seen there.
        */}
        {isApp && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-full -z-10 transition-transform"
            style={{
              transform: rowOpen ? "translateY(0)" : `translateY(-${CHROME_ROW_PX + CHROME_RAMP_PX}px)`,
              transitionDuration: `${CHROME_ROW.duration}s`,
              transitionTimingFunction: `cubic-bezier(${EASE.join(",")})`,
            }}
          >
            <div className="bg-background" style={{ height: CHROME_ROW_PX }} />
            <div style={{ height: CHROME_RAMP_PX, backgroundImage: CHROME_ROW_FADE }} />
          </div>
        )}

        {/*
          The second row's controls, right aligned under the first row's for the same reason the
          first row is (see below). The page fills it through PageHeaderRow; the row itself takes no
          pointer events, so the empty width of it never swallows a click meant for the page.
        */}
        {isApp && (
          <div
            id={PAGE_HEADER_ROW_SLOT_ID}
            ref={slotRef(PAGE_HEADER_ROW_SLOT_ID)}
            className={cn(
              "pointer-events-none absolute inset-x-0 top-full flex flex-row items-center justify-end gap-2",
              PAGE_INSET,
            )}
            style={{ height: CHROME_ROW_PX }}
          />
        )}

        {/*
          The page's controls, in all the room between the wordmark and the app's own buttons.

          `flex-1` so the slot's width is the header's free space rather than the width of what is
          in it: a page's `Toolbar` measures this to decide which of its groups fit, and folds the
          rest into an overflow button (see components/layout/Toolbar). That only works if the room
          it measures does not grow with its contents.

          A page that puts plain controls here rather than a toolbar is pushed right by `ml-auto` on
          its first child, and scrolls if it cannot fit — `ml-auto` and not `justify-end`, because a
          row justified to its end overflows off its start, where scrolling cannot reach.
        */}
        {isApp && (
          <div
            id={PAGE_HEADER_SLOT_ID}
            ref={slotRef(PAGE_HEADER_SLOT_ID)}
            className="flex min-w-0 flex-1 flex-row items-center overflow-x-auto py-4 [scrollbar-width:none] [&>*:first-child]:ml-auto"
          />
        )}

        {/*
          Between the page's controls and the app's own. Only while the page has put something in
          the slot — read from the DOM with `:has()`, so a page with no controls has no stray rule
          at the start of the app's buttons.
        */}
        {isApp && (
          // Not on a phone, where the page's controls and the app's share one ⋯ (see below).
          <ToolbarDivider className="hidden group-has-[#page-header-slot:not(:empty)]/header:block max-sm:!hidden" />
        )}

        <div
          className={cn(
            "flex shrink-0 flex-row items-center gap-2",
            !isApp && "ml-auto",
          )}
        >
          {/*
            The app's own buttons: in the row where there is room for them. On a phone the header
            is too narrow for them beside the page's controls, so they fold away — into the page's
            ⋯ when the page has a folding toolbar (see Toolbar), or into a ⋯ of the header's own
            when it does not. Either way a phone shows two buttons here: more, and the account.
          */}
          <div className="flex flex-row items-center gap-2 max-sm:hidden">
            <AppControls isApp={isApp} />
          </div>

          {/* The header's own ⋯, on a phone, for a page with no toolbar to fold the app's buttons into. */}
          <div className="hidden max-sm:flex max-sm:group-has-[[data-toolbar]]/header:hidden">
            <Popover>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <Button variant="secondary" size="icon" aria-label={tToolbar("moreControls")}>
                      <Ellipsis className="stroke-[2]" />
                    </Button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent>{tToolbar("moreControls")}</TooltipContent>
              </Tooltip>
              <PopoverContent
                align="end"
                sideOffset={12}
                className="w-auto rounded-4xl p-2"
                // Focus stays where it was, so no button's tooltip shows as it opens (see Toolbar).
                onOpenAutoFocus={(event) => event.preventDefault()}
                onCloseAutoFocus={(event) => event.preventDefault()}
              >
                <div className="flex flex-row items-center gap-2">
                  <AppControls isApp={isApp} />
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {user ? (
            <UserButton />
          ) : (
            // Sales open: no free accounts, so the primary CTA is the plans page, not
            // sign-up — an account only exists on the other side of a subscription.
            // Sales closed: the beta, where sign-up is the way in and needs no plan.
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="secondary" size="icon" asChild className="hidden sm:inline-flex">
                    <Link href="/sign-in">
                      <LogIn />
                    </Link>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t("signIn")}
                </TooltipContent>
              </Tooltip>


              {/*
                Subscribe while sales are open; Sign up while they are closed. Anyone who can
                see this header has already passed the access gate, so during the beta they are
                a tester, and the form at /sign-up gives them an account with no plan to choose.
                See config/launch.ts.
              */}
              <Button size="default" asChild>
                {SUBSCRIPTIONS_OPEN ? (
                  <Link href="/pricing">{t("subscribe")}</Link>
                ) : (
                  <Link href="/sign-up">{t("signUp")}</Link>
                )}
              </Button>
            </>
          )}
        </div>
      </header>

      {/* Mounted once for the whole app; its button, wherever it is, only opens it (see HistoryPanel). */}
      {user && <HistoryPanel />}
    </>
  )
}
